import React from 'react';
import Navigation from '../Navigation';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Separator } from '../ui/separator';
import { 
  Phone,
  Clock,
  Stethoscope,
  Settings,
  Play,
  Target,
  TrendingUp,
  Bot,
  RefreshCw,
  CalendarCheck,
  Activity,
  PoundSterling,
  Calendar,
  CheckCircle,
  BarChart3,
  Zap
} from 'lucide-react';

const VioConcierge = () => {
  const handleDemo = () => {
    window.open('https://cal.com/smartaisolutions/vioconcierge', '_blank');
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#112d56] via-[#1a3d6b] to-[#112d56] text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
        <div className="relative container mx-auto px-6 py-20 lg:py-32">
          <div className="max-w-6xl mx-auto text-center">
            <div className="space-y-6 lg:space-y-8">
              <h1 className="text-4xl lg:text-6xl xl:text-7xl font-bold leading-tight tracking-tight">
                VioConcierge: The <span className="text-[#ff7a1f]">Executive Standard</span> for Appointment Confirmations
              </h1>
              <p className="text-xl lg:text-2xl text-blue-100 leading-relaxed max-w-5xl mx-auto">
                Not every business is ready. The ones who are, lead the way. VioConcierge delivers premium outbound appointment confirmations that elevate your brand and eliminate no-shows.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
                <Button 
                  onClick={handleDemo}
                  size="lg"
                  className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-semibold px-8 py-6 text-lg rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
                >
                  <Play className="mr-3 h-6 w-6" />
                  Experience Executive-Level Service
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                Where <span className="text-[#ff7a1f]">Efficiency</span> Meets <span className="text-[#ff7a1f]">Prestige</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Proactive appointment confirmations that ensure your calendar stays full and your reputation stays pristine.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              <div className="relative">
                {/* Background decorative elements */}
                <div className="absolute -inset-4 bg-gradient-to-r from-[#ff7a1f]/20 to-[#112d56]/20 rounded-3xl blur-2xl opacity-30"></div>
                <div className="absolute -inset-2 bg-gradient-to-r from-[#ff7a1f]/10 to-[#112d56]/10 rounded-2xl blur-xl"></div>
                
                {/* Video Container */}
                <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
                  {/* Video Player Header */}
                  <div className="bg-gradient-to-r from-[#112d56] to-[#1a3d6b] px-6 py-4 flex items-center space-x-3">
                    <div className="flex space-x-2">
                      <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Play className="h-4 w-4 text-white/70" />
                    </div>
                  </div>
                  
                  {/* Video Embed - Using correct VioConcierge video ID */}
                  <div className="bg-black relative">
                    <div className="relative" style={{ paddingBottom: '56.25%' }}>
                      <iframe 
                        src="https://api.vadoo.tv/iframe_test?id=eFjiVTXPnvtStE5w2rmLJSQXXNhvZecM" 
                        scrolling="no" 
                        style={{ 
                          border: 0, 
                          top: 0, 
                          left: 0, 
                          width: '100%', 
                          height: '100%', 
                          position: 'absolute',
                          overflow: 'hidden',
                          borderRadius: '5px'
                        }} 
                        allowFullScreen="1" 
                        allow="autoplay"
                        title="VioConcierge Demo Video"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mt-12">
              <p className="text-lg text-gray-600 mb-4">
                Sophisticated outbound confirmations that eliminate no-shows while elevating your brand.
              </p>
              <Button 
                onClick={handleDemo}
                size="lg"
                className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-semibold px-8 py-6 text-lg rounded-xl"
              >
                See VioConcierge In Action
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* VioConcierge Appointment Efficiency Assessment Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                VioConcierge Appointment <span className="text-[#ff7a1f]">Efficiency Assessment</span>
              </h2>
              <h3 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-6">
                Tired of appointment no shows?
              </h3>
              <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
                Take our quick assessment to discover how to improve your appointment management system
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-8 mb-12">
              <h3 className="text-2xl font-bold text-[#112d56] mb-6 text-center">
                Why take the Appointment Efficiency Assessment?
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed text-center max-w-4xl mx-auto">
                You will gain insights into how you can improve your appointment management system. Take the <span className="font-semibold text-[#ff7a1f]">Appointment Efficiency Assessment</span> to benchmark your business, gain guidance on improvements, and reduce no-shows.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              
              {/* 5-Minute Analysis */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group text-center">
                <CardHeader className="pb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Clock className="h-8 w-8 text-[#ff7a1f]" />
                  </div>
                  <CardTitle className="text-xl font-bold text-[#112d56] mb-4">→ 5-MINUTE ANALYSIS</CardTitle>
                  <CardDescription className="text-gray-600 leading-relaxed">
                    Streamlined assessment process
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Industry Intelligence */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group text-center">
                <CardHeader className="pb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500/20 to-blue-500/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <BarChart3 className="h-8 w-8 text-blue-600" />
                  </div>
                  <CardTitle className="text-xl font-bold text-[#112d56] mb-4">→ INDUSTRY INTELLIGENCE</CardTitle>
                  <CardDescription className="text-gray-600 leading-relaxed">
                    Sector-specific benchmarking
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* AI-Powered Insights */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group text-center">
                <CardHeader className="pb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500/20 to-purple-500/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Zap className="h-8 w-8 text-purple-600" />
                  </div>
                  <CardTitle className="text-xl font-bold text-[#112d56] mb-4">→ AI-POWERED INSIGHTS</CardTitle>
                  <CardDescription className="text-gray-600 leading-relaxed">
                    Advanced analytics engine
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Assessment Report */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group text-center">
                <CardHeader className="pb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500/20 to-green-500/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <CardTitle className="text-xl font-bold text-[#112d56] mb-4">→ ASSESSMENT REPORT</CardTitle>
                  <CardDescription className="text-gray-600 leading-relaxed">
                    Detailed assessment report with recommendations
                  </CardDescription>
                </CardHeader>
              </Card>

            </div>

            <div className="text-center">
              <Button 
                size="lg"
                className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-bold px-8 py-6 text-xl rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
                onClick={() => window.open('https://assessment.vioconcierge.com', '_blank')}
              >
                <BarChart3 className="mr-3 h-6 w-6" />
                Take Appointment Efficiency Assessment
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Forward-Thinking Businesses Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                The Signature Service of <span className="text-[#ff7a1f]">Forward-Thinking Businesses</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
                Most appointment businesses still rely on email or sms Leaders don't. VioConcierge is for businesses ready to elevate their client experience and reputation.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              
              {/* Executive Medical Practices */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group bg-gradient-to-br from-white to-gray-50">
                <CardHeader className="pb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500/20 to-blue-500/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Stethoscope className="h-8 w-8 text-blue-600" />
                  </div>
                  <CardTitle className="text-xl font-bold text-[#112d56] text-center mb-4">Executive Medical Practices</CardTitle>
                  <CardDescription className="text-gray-600 text-center leading-relaxed">
                    Premium healthcare providers who proactively confirm patient appointments with sophisticated outbound calls. Reduce no-shows while maintaining the professional standard your patients expect.
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* High-End Professional Services */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group bg-gradient-to-br from-white to-gray-50">
                <CardHeader className="pb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500/20 to-purple-500/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Target className="h-8 w-8 text-purple-600" />
                  </div>
                  <CardTitle className="text-xl font-bold text-[#112d56] text-center mb-4">Elite Professional Services</CardTitle>
                  <CardDescription className="text-gray-600 text-center leading-relaxed">
                    Top-tier legal, consulting, and financial firms where client meetings are high-value. Executive-level appointment confirmations ensure maximum attendance and protect billable hours.
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Luxury Beauty & Wellness */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group bg-gradient-to-br from-white to-gray-50">
                <CardHeader className="pb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-500/20 to-pink-500/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Settings className="h-8 w-8 text-pink-600" />
                  </div>
                  <CardTitle className="text-xl font-bold text-[#112d56] text-center mb-4">Luxury Beauty & Wellness</CardTitle>
                  <CardDescription className="text-gray-600 text-center leading-relaxed">
                    Premium salons and spas where client retention is everything. Sophisticated pre-appointment confirmations reduce no-shows while maintaining the elegant experience clients expect.
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Premium Service Companies */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group bg-gradient-to-br from-white to-gray-50">
                <CardHeader className="pb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500/20 to-green-500/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Clock className="h-8 w-8 text-green-600" />
                  </div>
                  <CardTitle className="text-xl font-bold text-[#112d56] text-center mb-4">Premium Service Companies</CardTitle>
                  <CardDescription className="text-gray-600 text-center leading-relaxed">
                    High-end service providers who understand that reputation is built on reliability. VioConcierge ensures your clients never question your professionalism.
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Executive Consultancy */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group bg-gradient-to-br from-white to-gray-50">
                <CardHeader className="pb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-indigo-500/20 to-indigo-500/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <TrendingUp className="h-8 w-8 text-indigo-600" />
                  </div>
                  <CardTitle className="text-xl font-bold text-[#112d56] text-center mb-4">Executive Consultancy</CardTitle>
                  <CardDescription className="text-gray-600 text-center leading-relaxed">
                    Strategic advisory firms where time is money and reputation is everything. VioConcierge protects both with seamless, intelligent appointment management.
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Exclusive Hospitality */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group bg-gradient-to-br from-white to-gray-50">
                <CardHeader className="pb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500/20 to-orange-500/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Phone className="h-8 w-8 text-orange-600" />
                  </div>
                  <CardTitle className="text-xl font-bold text-[#112d56] text-center mb-4">Exclusive Hospitality</CardTitle>
                  <CardDescription className="text-gray-600 text-center leading-relaxed">
                    Fine dining and luxury hospitality where every guest interaction shapes your reputation. VioConcierge ensures every reservation reflects your commitment to excellence.
                  </CardDescription>
                </CardHeader>
              </Card>

            </div>

            <div className="text-center mt-16">
              <div className="bg-gradient-to-r from-[#112d56] to-[#1a3d6b] text-white p-8 rounded-2xl max-w-4xl mx-auto">
                <p className="text-2xl font-semibold mb-4">
                  "This isn't for everyone — it's for the innovators."
                </p>
                <p className="text-xl text-blue-100">
                  Like a luxury hotel concierge, but for your business.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How VioConcierge Transforms Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                How VioConcierge <span className="text-[#ff7a1f]">Transforms</span> Your Business
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Advanced Voice AI technology that delivers measurable results for appointment management
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              
              {/* Advanced Conversational AI */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
                <CardHeader className="pb-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-xl flex items-center justify-center group-hover:from-[#ff7a1f]/30 group-hover:to-[#ff7a1f]/10 transition-all duration-300">
                      <Bot className="h-6 w-6 text-[#ff7a1f]" />
                    </div>
                    <CardTitle className="text-xl font-bold text-[#112d56]">Advanced Conversational AI with Call Outcome Intelligence</CardTitle>
                  </div>
                  <CardDescription className="text-gray-700 text-base leading-relaxed">
                    Get definitive confirmations, not just reminders. Our AI gets clear "yes," "no," or "reschedule" responses from every client, so you know exactly who's showing up before they walk through your door.
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Fully Automated Follow-Up System */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
                <CardHeader className="pb-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-xl flex items-center justify-center group-hover:from-[#ff7a1f]/30 group-hover:to-[#ff7a1f]/10 transition-all duration-300">
                      <RefreshCw className="h-6 w-6 text-[#ff7a1f]" />
                    </div>
                    <CardTitle className="text-xl font-bold text-[#112d56]">Fully Automated Follow-Up System</CardTitle>
                  </div>
                  <CardDescription className="text-gray-700 text-base leading-relaxed">
                    Eliminate hours of manual calling each week. Your staff stops chasing clients for confirmations. Our system automatically follows up with no-answers and voicemails within one hour, handling the entire confirmation process without any human intervention.
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Real-Time Calendar Sync */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
                <CardHeader className="pb-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-xl flex items-center justify-center group-hover:from-[#ff7a1f]/30 group-hover:to-[#ff7a1f]/10 transition-all duration-300">
                      <CalendarCheck className="h-6 w-6 text-[#ff7a1f]" />
                    </div>
                    <CardTitle className="text-xl font-bold text-[#112d56]">Real-Time Calendar Sync with Automatic Rescheduling</CardTitle>
                  </div>
                  <CardDescription className="text-gray-700 text-base leading-relaxed">
                    Fill cancelled slots automatically. When clients need to reschedule, our AI handles it instantly during the call and updates your calendar in real-time. No more empty slots that could have been filled if you'd known sooner.
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Real-Time Performance Analytics */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
                <CardHeader className="pb-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-xl flex items-center justify-center group-hover:from-[#ff7a1f]/30 group-hover:to-[#ff7a1f]/10 transition-all duration-300">
                      <Activity className="h-6 w-6 text-[#ff7a1f]" />
                    </div>
                    <CardTitle className="text-xl font-bold text-[#112d56]">Real-Time Performance Analytics Dashboard</CardTitle>
                  </div>
                  <CardDescription className="text-gray-700 text-base leading-relaxed">
                    Track your revenue recovery in real-time. See exactly how much money you're saving from reduced no-shows with live analytics. Watch your confirmation rates climb and measure the precise ROI of every recovered appointment.
                  </CardDescription>
                </CardHeader>
              </Card>

            </div>
          </div>
        </div>
      </section>

      {/* Automated Appointment Booking Pipeline Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                Automated Appointment <span className="text-[#ff7a1f]">Booking Pipeline</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                VioConcierge works effortlessly with your existing booking systems for complete workflow automation
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-12">
              
              {/* Cal.com Integration */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardHeader className="text-center">
                  <div className="text-6xl mb-4">📅</div>
                  <CardTitle className="text-2xl font-bold text-[#112d56] mb-2">Cal.com</CardTitle>
                  <div className="text-[#ff7a1f] text-lg font-semibold mb-4">↕️ VioConcierge</div>
                  <CardTitle className="text-xl font-bold text-gray-800 mb-4">Cal.com Automatic Sync</CardTitle>
                  <CardDescription className="text-gray-600 leading-relaxed mb-6">
                    Every booking on your Cal.com calendar automatically becomes a confirmed appointment - zero manual data entry or missed calls.
                  </CardDescription>
                  
                  <div className="space-y-2 text-left">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-gray-700">Instant booking detection</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-gray-700">Automatic confirmation calls</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-gray-700">Real-time calendar updates</span>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              {/* Calendly Integration */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardHeader className="text-center">
                  <div className="text-6xl mb-4">📊</div>
                  <CardTitle className="text-2xl font-bold text-[#112d56] mb-2">Calendly</CardTitle>
                  <div className="text-[#ff7a1f] text-lg font-semibold mb-4">↕️ VioConcierge</div>
                  <CardTitle className="text-xl font-bold text-gray-800 mb-4">Calendly Integration</CardTitle>
                  <CardDescription className="text-gray-600 leading-relaxed mb-6">
                    Enterprise calendar platform users get instant VioConcierge automation - professional booking flow meets professional confirmation system.
                  </CardDescription>
                  
                  <div className="space-y-2 text-left">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-gray-700">Enterprise-grade security</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-gray-700">Multi-platform compatibility</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-gray-700">Streamlined booking process</span>
                    </div>
                  </div>
                </CardHeader>
              </Card>

            </div>
          </div>
        </div>
      </section>

      {/* The Business Impact of Missed Appointments */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                The Business Impact of <span className="text-[#ff7a1f]">Missed Appointments</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Understanding the operational and financial consequences of poor appointment management
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              
              {/* £18,000+ Annual Revenue Loss */}
              <Card className="bg-gradient-to-br from-white to-gray-50 border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden group">
                <div className="h-2 bg-gradient-to-r from-red-500 to-red-600"></div>
                <CardHeader className="text-center pb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-red-500/20 to-red-500/5 rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <PoundSterling className="h-10 w-10 text-red-600" />
                  </div>
                  <div className="text-4xl font-bold text-gray-900 mb-2">£18,000+</div>
                  <CardTitle className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Annual Revenue Loss</CardTitle>
                  <CardDescription className="text-gray-600 text-sm">
                    Per UK business from appointment no-shows. 20% average rate with £160 appointment value compounds monthly across operations.
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* 2-4h Daily Staff Time */}
              <Card className="bg-gradient-to-br from-white to-gray-50 border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden group">
                <div className="h-2 bg-gradient-to-r from-orange-500 to-orange-600"></div>
                <CardHeader className="text-center pb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-orange-500/20 to-orange-500/5 rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Clock className="h-10 w-10 text-orange-600" />
                  </div>
                  <div className="text-4xl font-bold text-gray-900 mb-2">2-4h</div>
                  <CardTitle className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Daily Staff Time</CardTitle>
                  <CardDescription className="text-gray-600 text-sm">
                    Manual confirmation calls consuming productive hours that could be spent on patient care and revenue-generating activities.
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* 78% Voice Response Rate */}
              <Card className="bg-gradient-to-br from-white to-gray-50 border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden group">
                <div className="h-2 bg-gradient-to-r from-blue-500 to-blue-600"></div>
                <CardHeader className="text-center pb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500/20 to-blue-500/5 rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Phone className="h-10 w-10 text-blue-600" />
                  </div>
                  <div className="text-4xl font-bold text-gray-900 mb-2">78%</div>
                  <CardTitle className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Voice Response Rate</CardTitle>
                  <CardDescription className="text-gray-600 text-sm">
                    Voice calling delivers 4.3× higher engagement rates than SMS, ensuring better confirmation rates and customer connection.
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* 23% Efficiency Loss */}
              <Card className="bg-gradient-to-br from-white to-gray-50 border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden group">
                <div className="h-2 bg-gradient-to-r from-purple-500 to-purple-600"></div>
                <CardHeader className="text-center pb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-500/20 to-purple-500/5 rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <TrendingUp className="h-10 w-10 text-purple-600" />
                  </div>
                  <div className="text-4xl font-bold text-gray-900 mb-2">23%</div>
                  <CardTitle className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Efficiency Loss</CardTitle>
                  <CardDescription className="text-gray-600 text-sm">
                    Poor appointment management creates workflow disruption that impacts overall operational efficiency and staff productivity.
                  </CardDescription>
                </CardHeader>
              </Card>

            </div>
          </div>
        </div>
      </section>

      {/* Operational Metrics & Industry Benchmarks */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                Operational Metrics & <span className="text-[#ff7a1f]">Industry Benchmarks</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Key performance indicators across appointment-based businesses
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              
              {/* 10-15% Typical No-Show Rate */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardHeader className="pb-4">
                  <CardTitle className="text-2xl font-bold text-[#112d56] flex items-center space-x-3">
                    <span className="text-5xl font-bold text-red-600">10-15%</span>
                    <div className="text-right">
                      <div className="text-sm text-gray-500 uppercase tracking-wide">Typical No-Show Rate</div>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Industry Average for Small Businesses</h3>
                  <p className="text-gray-600 leading-relaxed">
                    The average no-show rate across small businesses is 10–15%, meaning roughly one in seven clients never shows up unless a system is in place to confirm or reschedule.
                  </p>
                </CardContent>
              </Card>

              {/* 40M Minutes Daily */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardHeader className="pb-4">
                  <CardTitle className="text-2xl font-bold text-[#112d56] flex items-center space-x-3">
                    <span className="text-5xl font-bold text-orange-600">40M</span>
                    <div className="text-right">
                      <div className="text-sm text-gray-500 uppercase tracking-wide">Minutes Daily</div>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Manual Booking & Follow-ups</h3>
                  <p className="text-gray-600 leading-relaxed">
                    UK appointment-based SMEs spend around 20 million minutes every day coordinating bookings. Including client chasing and follow-ups, this climbs to over 40 million minutes daily.
                  </p>
                </CardContent>
              </Card>

              {/* 8.1 Minutes Wasted */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardHeader className="pb-4">
                  <CardTitle className="text-2xl font-bold text-[#112d56] flex items-center space-x-3">
                    <span className="text-5xl font-bold text-purple-600">8.1</span>
                    <div className="text-right">
                      <div className="text-sm text-gray-500 uppercase tracking-wide">Minutes Wasted</div>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Administrative Overhead per No-Show</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Each no-show wastes about 8.1 minutes of staff time. For a business with an 18.8% no-show rate, that equals 31.9 minutes per provider per day lost to scheduling work.
                  </p>
                </CardContent>
              </Card>

              {/* 130h Lost Annually */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardHeader className="pb-4">
                  <CardTitle className="text-2xl font-bold text-[#112d56] flex items-center space-x-3">
                    <span className="text-5xl font-bold text-blue-600">130h</span>
                    <div className="text-right">
                      <div className="text-sm text-gray-500 uppercase tracking-wide">Lost Annually</div>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Workforce Efficiency Impact</h3>
                  <p className="text-gray-600 leading-relaxed">
                    40% of employees waste about 30 minutes each day managing calendars and appointments. Over a year, that's about 130 hours of lost productivity per employee.
                  </p>
                </CardContent>
              </Card>

            </div>
          </div>
        </div>
      </section>

      {/* Premium Experience Invitation Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
              Ready to Elevate Your Client Experience?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              VioConcierge isn't a tool. It's a statement. It tells your clients you value their time and your reputation.
            </p>
            
            <div className="text-xl text-gray-600 mb-8">Your exclusive consultation includes:</div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              
              {/* Executive Demonstration */}
              <Card className="border-0 shadow-lg text-center">
                <CardHeader>
                  <div className="text-4xl mb-4">🎯</div>
                  <CardTitle className="text-xl font-bold text-[#112d56] mb-4">Executive Demonstration</CardTitle>
                  <CardDescription className="text-gray-600 leading-relaxed">
                    Experience the concierge-level service that sets industry leaders apart from their competitors.
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Prestige Assessment */}
              <Card className="border-0 shadow-lg text-center">
                <CardHeader>
                  <div className="text-4xl mb-4">📊</div>
                  <CardTitle className="text-xl font-bold text-[#112d56] mb-4">Prestige Assessment</CardTitle>
                  <CardDescription className="text-gray-600 leading-relaxed">
                    Discover how VioConcierge can enhance your brand reputation and client perception.
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Strategic Consultation */}
              <Card className="border-0 shadow-lg text-center">
                <CardHeader>
                  <div className="text-4xl mb-4">✅</div>
                  <CardTitle className="text-xl font-bold text-[#112d56] mb-4">Strategic Consultation</CardTitle>
                  <CardDescription className="text-gray-600 leading-relaxed">
                    Determine if your business is ready for the executive standard in appointment management.
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Implementation Excellence */}
              <Card className="border-0 shadow-lg text-center">
                <CardHeader>
                  <div className="text-4xl mb-4">🚀</div>
                  <CardTitle className="text-xl font-bold text-[#112d56] mb-4">Implementation Excellence</CardTitle>
                  <CardDescription className="text-gray-600 leading-relaxed">
                    Learn how quickly you can begin delivering the concierge experience your clients deserve.
                  </CardDescription>
                </CardHeader>
              </Card>

            </div>

            <div className="text-center">
              <p className="text-lg text-gray-600 mb-8">The quiet advantage your competitors don't have.</p>
              <Button 
                onClick={handleDemo}
                size="lg"
                className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-bold px-8 py-6 text-xl rounded-xl"
              >
                <Calendar className="mr-3 h-6 w-6" />
                Book a VioConcierge demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#112d56] text-white py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-1">
              <div className="flex items-center space-x-2 mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_vioq-content-update/artifacts/w40y3694_Smart%20AI%20Logo%20white.png"
                  alt="Smart AI Solutions"
                  className="h-10 w-auto"
                />
              </div>
              <p className="text-blue-200 leading-relaxed mb-8 max-w-xs">
                Driving efficiency and customer excellence with VioSuite — AI-powered voice & knowledge solutions for modern business.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Product</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/features" className="hover:text-[#ff7a1f] transition-colors text-lg">Features</a></li>
                <li><a href="/use-cases" className="hover:text-[#ff7a1f] transition-colors text-lg">Use Cases</a></li>
                <li><a href="/products/vioreceptionist" className="hover:text-[#ff7a1f] transition-colors text-lg">VioReceptionist</a></li>
                <li><a href="/products/viocoach" className="hover:text-[#ff7a1f] transition-colors text-lg">VioCoach</a></li>
                <li><a href="/products/vioconcierge" className="hover:text-[#ff7a1f] transition-colors text-lg">VioConcierge</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Company</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/about" className="hover:text-[#ff7a1f] transition-colors text-lg">About</a></li>
                <li><a href="/contact" className="hover:text-[#ff7a1f] transition-colors text-lg">Contact</a></li>
                <li><a href="https://cal.com/smartaisolutions/vioconcierge" target="_blank" rel="noopener noreferrer" className="hover:text-[#ff7a1f] transition-colors text-lg">Book Demo</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Legal</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/privacy" className="hover:text-[#ff7a1f] transition-colors text-lg">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-[#ff7a1f] transition-colors text-lg">Terms of Service</a></li>
                <li><a href="/cookies" className="hover:text-[#ff7a1f] transition-colors text-lg">Cookie Policy</a></li>
              </ul>
            </div>
          </div>
          
          <Separator className="bg-blue-800/50 my-8" />
          
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-blue-200 text-lg">
              © 2025 VioQbase. All rights reserved.
            </p>
            <div className="flex space-x-8 text-blue-200 mt-4 md:mt-0">
              <a href="/privacy" className="hover:text-[#ff7a1f] transition-colors text-lg">Privacy Policy</a>
              <a href="/terms" className="hover:text-[#ff7a1f] transition-colors text-lg">Terms of Service</a>
              <a href="/cookies" className="hover:text-[#ff7a1f] transition-colors text-lg">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VioConcierge;